import type { MutationKey, UseMutationOptions } from '@tanstack/react-query';
import {
  CustomerUpdateEmailParams,
  updateEmail,
  CustomerSendPasswordResetEmailParams,
  sendPasswordResetEmail,
  CustomerCreateAccountParams,
  createAccount,
  addDeliveryAddress,
  updateDeliveryAddress,
  updatePhoneNumber,
  updateProfile,
} from './customer.api';
import type { OptionalMutationOptions, QueryApiOptions } from '../../types/types';
import { CustomerAddress, CustomerProfile } from '@as-react/common-types';
import { AxiosResponse } from 'axios';

export const customerMutationKeys = {
  all: () => ['customer'] as MutationKey,
  updateEmail: () => [...customerMutationKeys.all(), 'updateEmail'] as MutationKey,
  sendPasswordResetEmail: () => [...customerMutationKeys.all(), 'sendPasswordResetMail'] as MutationKey,
  createAccount: () => [...customerMutationKeys.all(), 'createAccount'] as MutationKey,
  addDeliveryAddress: () => [...customerMutationKeys.all(), 'addDeliveryAddress'] as MutationKey,
  updateDeliveryAddress: () => [...customerMutationKeys.all(), 'updateDeliveryAddress'] as MutationKey,
  updatePhoneNumber: () => [...customerMutationKeys.all(), 'updatePhoneNumber'] as MutationKey,
  updateProfile: () => [...customerMutationKeys.all(), 'updateProfile'] as MutationKey,
};

export const customerMutations = (apiOptions: QueryApiOptions) => ({
  updateEmailMutation: (
    options: OptionalMutationOptions<undefined, CustomerUpdateEmailParams> = {}
  ): UseMutationOptions<undefined, Error, CustomerUpdateEmailParams> => ({
    ...options,
    mutationKey: customerMutationKeys.updateEmail(),
    mutationFn: (params: CustomerUpdateEmailParams) => updateEmail(apiOptions, params),
  }),
  sendPasswordResetEmailMutation: (
    options: OptionalMutationOptions<undefined, CustomerSendPasswordResetEmailParams> = {}
  ): UseMutationOptions<undefined, Error, CustomerSendPasswordResetEmailParams> => ({
    ...options,
    mutationKey: customerMutationKeys.sendPasswordResetEmail(),
    mutationFn: (params: CustomerSendPasswordResetEmailParams) => sendPasswordResetEmail(apiOptions, params),
  }),
  createAccountMutation: (
    options: OptionalMutationOptions<boolean, CustomerCreateAccountParams> = {}
  ): UseMutationOptions<boolean, Error, CustomerCreateAccountParams> => ({
    ...options,
    mutationKey: customerMutationKeys.createAccount(),
    mutationFn: (params: CustomerCreateAccountParams) => createAccount(apiOptions, params),
  }),
  addDeliveryAddressMutation: (
    options: OptionalMutationOptions<unknown, CustomerAddress> = {}
  ): UseMutationOptions<CustomerAddress, Error, CustomerAddress> => ({
    ...options,
    mutationKey: customerMutationKeys.addDeliveryAddress(),
    mutationFn: (params: CustomerAddress) => addDeliveryAddress(apiOptions, params),
  }),
  updateDeliveryAddressMutation: (
    options: OptionalMutationOptions<unknown, CustomerAddress> = {}
  ): UseMutationOptions<CustomerAddress, Error, CustomerAddress> => ({
    ...options,
    mutationKey: customerMutationKeys.updateDeliveryAddress(),
    mutationFn: (params: CustomerAddress) => updateDeliveryAddress(apiOptions, params),
  }),
  updatePhoneNumber: (
    options: OptionalMutationOptions<unknown, string> = {}
  ): UseMutationOptions<AxiosResponse, Error, string> => ({
    ...options,
    mutationKey: customerMutationKeys.updatePhoneNumber(),
    mutationFn: (phone: string) => updatePhoneNumber(apiOptions, phone),
  }),
  updateProfileMutation: (
    options: OptionalMutationOptions<unknown, CustomerProfile> = {}
  ): UseMutationOptions<CustomerProfile, Error, CustomerProfile> => ({
    ...options,
    mutationKey: customerMutationKeys.updateProfile(),
    mutationFn: (profile: CustomerProfile) => updateProfile(apiOptions, profile),
  }),
});
